import {
  MessageSquare,
  AudioLines,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Search,
  Settings2,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTestMode } from '../components/testMode/TestModeContext';
import { VoiceSettingsPanel } from '../components/voice/VoiceSettingsPanel';
import { useI18n } from '../contexts/I18nContext';
import { useVoiceAssistant, type VoiceErrorKind } from '../hooks/useVoiceAssistant';
import { getVoiceAssistantStatus, type VoiceAssistantStatus } from '../lib/voiceAssistantApi';

/** GPT-Live audio is $0.05/min; the same pessimistic USD→EUR rate as the backend. */
const EUR_PER_MINUTE = 0.05 * 0.95;

/** Human labels for the activity chip while a Firmio tool runs on the server. */
const TOOL_LABELS: Record<string, string> = {
  kb_semantic_search: 'Knowledge Base',
  kb_read_file: 'Knowledge Base',
  kb_list_files: 'Knowledge Base',
  kb_read_projects: 'proiecte',
  kb_read_people: 'echipă',
  kb_read_financials: 'financiar',
  get_project_context: 'proiect',
  task_query: 'taskuri',
  task_create: 'taskuri',
  lead_list: 'lead-uri',
  lead_stats: 'lead-uri',
  meeting_search: 'întâlniri',
  meeting_list: 'întâlniri',
  meeting_note_search: 'notițe',
  calendar_get_events: 'calendar',
  reminder_query: 'remindere',
  reminder_create: 'remindere',
  invoice_query: 'facturi',
  financial_report: 'financiar',
  supplier_query: 'furnizori',
  bank_transaction_query: 'extras bancar',
  cashflow_projection: 'cash flow',
  remember: 'memorie',
};

const ERROR_KEYS: Record<VoiceErrorKind, string> = {
  'mic-denied': 'voice.error.micDenied',
  'mic-unavailable': 'voice.error.micUnavailable',
  'not-configured': 'voice.error.notConfigured',
  'no-credits': 'voice.error.noCredits',
  budget: 'voice.error.budget',
  busy: 'voice.error.busy',
  network: 'voice.error.network',
  generic: 'voice.error.generic',
};

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * /voice — Firmio Voice, the spoken Firmio AI. One big button starts a
 * GPT-Live session; captions, the activity chip and the timer come from the
 * live event stream; the transcript lands in the Firmio AI history.
 */
export function VoiceAssistantPage() {
  const { t } = useI18n();
  const { enabled: demoMode } = useTestMode();
  const [status, setStatus] = useState<VoiceAssistantStatus | null>(null);
  const voice = useVoiceAssistant();
  const captionsRef = useRef<HTMLDivElement | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (demoMode) {
      setStatus({ enabled: false, model: '', backendModel: '', voice: '', maxMinutes: 0 });
      return;
    }
    void getVoiceAssistantStatus()
      .then(setStatus)
      .catch(() =>
        setStatus({ enabled: false, model: '', backendModel: '', voice: '', maxMinutes: 0 }),
      );
  }, [demoMode]);

  // Keep the newest caption in view.
  useEffect(() => {
    const el = captionsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [voice.captions]);

  const busy =
    voice.phase === 'requesting-mic' || voice.phase === 'connecting' || voice.phase === 'ending';
  const live = voice.phase === 'live';
  const canStart =
    Boolean(status?.enabled) &&
    !demoMode &&
    (voice.phase === 'idle' || voice.phase === 'ended' || voice.phase === 'error');

  const activityLabel = useMemo(() => {
    if (!live) return null;
    if (voice.activity === 'tool') {
      const label = voice.activeTool ? TOOL_LABELS[voice.activeTool] : undefined;
      return label ? t('voice.activity.toolNamed', { label }) : t('voice.activity.tool');
    }
    if (voice.activity === 'thinking') return t('voice.activity.thinking');
    return null;
  }, [live, t, voice.activity, voice.activeTool]);

  const phaseLabel = (() => {
    switch (voice.phase) {
      case 'requesting-mic':
        return t('voice.phase.requestingMic');
      case 'connecting':
        return t('voice.phase.connecting');
      case 'live':
        return t('voice.phase.live');
      case 'ending':
        return t('voice.phase.ending');
      case 'ended':
        return t('voice.phase.ended');
      case 'error':
        return t('voice.phase.error');
      default:
        return t('voice.phase.idle');
    }
  })();

  const closeReasonText = (() => {
    if (voice.phase !== 'ended') return null;
    switch (voice.closeReason) {
      case 'expired':
      case 'max_duration':
        return t('voice.closeReason.expired');
      case 'budget':
        return t('voice.closeReason.budget');
      case 'connection_lost':
        return t('voice.closeReason.connectionLost');
      default:
        return null;
    }
  })();

  const estimatedCost = ((voice.seconds / 60) * EUR_PER_MINUTE).toFixed(2);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <AudioLines size={18} className="shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-heading text-base font-semibold text-foreground">
            {t('voice.title')}
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            {status?.enabled
              ? t('voice.modelLabel', {
                  model: status.model,
                  backend: status.backendModel,
                  voice: capitalize(status.voice),
                })
              : t('voice.subtitle')}
          </p>
        </div>
        <Link
          to="/chat"
          className="hidden items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground sm:flex"
        >
          <MessageSquare size={14} />
          {t('voice.backToChat')}
        </Link>
        <button
          type="button"
          onClick={() => setShowSettings((v) => !v)}
          className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
            showSettings
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
          title={t('voice.settings.title')}
        >
          <Settings2 size={14} />
          <span className="hidden sm:inline">
            {t('voice.settings.button', { voice: status?.voice ? capitalize(status.voice) : '…' })}
          </span>
        </button>
        {(live || busy) && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums" title={t('voice.elapsed')}>
              {formatDuration(voice.seconds)}
              {status?.maxMinutes ? ` / ${status.maxMinutes}:00` : ''}
            </span>
            <span className="hidden sm:inline" title={t('voice.cost')}>
              ≈ {estimatedCost} €
            </span>
          </div>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 py-4">
        {/* Not-configured / demo notice */}
        {status && !status.enabled && (
          <div className="shrink-0 rounded-lg border border-border bg-card p-4 text-sm text-card-foreground">
            {demoMode ? t('voice.demoMode') : t('voice.notConfigured')}
          </div>
        )}

        {/* Error */}
        {voice.phase === 'error' && voice.error && (
          <div className="shrink-0 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground">
            {t(ERROR_KEYS[voice.error])}
          </div>
        )}

        {/* Ended summary */}
        {voice.phase === 'ended' && (
          <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card p-4 text-sm text-card-foreground">
            <span>
              {t('voice.endedSummary', { duration: formatDuration(voice.seconds) })}
              {closeReasonText ? ` ${closeReasonText}` : ''}
            </span>
            {voice.pendingSuggestions > 0 && (
              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {t('voice.endedSuggestions', { count: voice.pendingSuggestions })}
              </span>
            )}
            {voice.session && (voice.captions.length > 0 || voice.pendingSuggestions > 0) && (
              <Link
                to={`/chat/${voice.session.conversationId}`}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {voice.pendingSuggestions > 0
                  ? t('voice.reviewSuggestions')
                  : t('voice.viewConversation')}
              </Link>
            )}
          </div>
        )}

        {/* Voice picker (replaces the transcript while open) */}
        {showSettings && (
          <VoiceSettingsPanel
            sessionLive={live || busy}
            onClose={() => setShowSettings(false)}
            onSelected={(picked) => setStatus((s) => (s ? { ...s, voice: picked } : s))}
          />
        )}

        {/* Captions */}
        <div
          hidden={showSettings}
          ref={captionsRef}
          className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-background p-4"
        >
          {voice.captions.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                <Sparkles size={22} className="text-accent-foreground" />
              </span>
              <p className="max-w-md">
                {live ? t('voice.captions.listening') : t('voice.captions.empty')}
              </p>
              {!live && (
                <ul className="max-w-md space-y-1 text-xs">
                  <li>„{t('voice.example1')}”</li>
                  <li>„{t('voice.example2')}”</li>
                  <li>„{t('voice.example3')}”</li>
                </ul>
              )}
            </div>
          ) : (
            <ul className="space-y-3">
              {voice.captions.map((c) => (
                <li
                  key={c.id}
                  className={`flex ${c.speaker === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                      c.speaker === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-card-foreground border border-border'
                    }`}
                  >
                    <div className="mb-0.5 text-[10px] uppercase tracking-wide opacity-70">
                      {c.speaker === 'user'
                        ? t('voice.captions.you')
                        : t('voice.captions.assistant')}
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{c.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Controls */}
        <div className="flex shrink-0 flex-col items-center gap-3">
          <div className="flex h-6 items-center gap-2 text-xs text-muted-foreground">
            {busy && <Loader2 size={14} className="animate-spin" />}
            {activityLabel ? (
              <span className="flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-accent-foreground">
                {voice.activity === 'tool' ? <Search size={12} /> : <Sparkles size={12} />}
                {activityLabel}
              </span>
            ) : (
              <span>{phaseLabel}</span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {live && (
              <button
                type="button"
                onClick={voice.toggleMute}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-foreground hover:bg-accent"
                title={voice.muted ? t('voice.unmute') : t('voice.mute')}
                aria-label={voice.muted ? t('voice.unmute') : t('voice.mute')}
              >
                {voice.muted ? <MicOff size={20} /> : <Mic size={20} />}
              </button>
            )}

            {live || busy ? (
              <button
                type="button"
                onClick={() => void voice.end()}
                disabled={voice.phase === 'ending' || voice.phase === 'requesting-mic'}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-lg transition hover:opacity-90 disabled:opacity-50"
                title={t('voice.end')}
                aria-label={t('voice.end')}
              >
                <PhoneOff size={26} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void voice.start()}
                disabled={!canStart}
                className={`flex h-16 w-16 items-center justify-center rounded-full text-primary-foreground shadow-lg transition ${
                  canStart ? 'bg-primary hover:opacity-90' : 'bg-muted text-muted-foreground'
                }`}
                title={t('voice.start')}
                aria-label={t('voice.start')}
              >
                <Mic size={26} />
              </button>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {live
              ? t('voice.hintLive')
              : voice.phase === 'ended'
                ? t('voice.newSession')
                : t('voice.start')}
          </p>
        </div>
      </div>
    </div>
  );
}
